import { LoadingSpinner, PlainButton, Tooltip } from "@jugl-web/ui-kit";
import { cx, useToast, useTranslations } from "@jugl-web/utils";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { collapsedIconButtonClassName } from "@web-src/modules/client-support/pages/ClientSupportPage/consts";
import { useEmailAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/EmailAccountProvider/useEmailAccount";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { useCallback } from "react";
import {
  CRMStatus,
  CRMStatusPill,
} from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/components/CRMStatusPill";
import { ReactComponent as RefreshIcon } from "./assets/refresh.svg";
import { ReactComponent as SetupIcon } from "./assets/setup.svg";

export const DomainAuthCell: React.FC<{
  domainAuthStatus: CRMStatus | null;
  onSetupClick: () => void;
}> = ({ domainAuthStatus, onSetupClick }) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { entityId } = useEntitySelectedProvider();
  const { emailAccount } = useEmailAccount();
  const { integrationsApi } = useRestApiProvider();
  const [updateEmailConfig, { isLoading: isEnablingDomainAuth }] =
    integrationsApi.useUpdateEmailConfigMutation();
  const [getEmailConfig, { isFetching: isEmailConfigFetching }] =
    integrationsApi.useLazyGetEmailConfigQuery();

  const handleAuthenticate = useCallback(async () => {
    try {
      await updateEmailConfig({
        entityId,
        data: {
          metadata: { ...emailAccount?.metadata, domain_auth_enabled: true },
        },
        meta: { silent: true },
      }).unwrap();

      onSetupClick();
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    }
  }, [emailAccount, entityId, onSetupClick, t, toast, updateEmailConfig]);

  return (
    <div className="flex w-full items-center justify-between gap-2">
      {domainAuthStatus ? (
        <Tooltip
          renderTrigger={({ props, ref }) => (
            <div ref={ref} {...props} className="cursor-default">
              <CRMStatusPill status={domainAuthStatus} size="sm" />
            </div>
          )}
        >
          {domainAuthStatus === "verified"
            ? t({
                id: "client-support-page.refresh-auth-status-info",
                defaultMessage: "Refresh to check the latest status ℹ️",
              })
            : t({
                id: "client-support-page.auth-status-info",
                defaultMessage:
                  "This may take a few hours, depends on your Domain Provider ℹ️",
              })}
        </Tooltip>
      ) : (
        <PlainButton
          className="px-3 font-secondary text-xs font-medium text-primary-800 underline"
          onClick={handleAuthenticate}
          disabled={isEnablingDomainAuth}
        >
          {t({
            id: "client-support-page.authenticate",
            defaultMessage: "Authenticate",
          })}
        </PlainButton>
      )}
      {domainAuthStatus && (
        <div className="flex items-center gap-3">
          <Tooltip
            renderTrigger={({ props, ref }) => (
              <PlainButton
                className={cx(collapsedIconButtonClassName, "h-8 w-8")}
                ref={ref}
                {...props}
                onClick={onSetupClick}
              >
                <SetupIcon className="h-4.5 w-4.5" />
              </PlainButton>
            )}
          >
            {t({
              id: "client-support-page.setup",
              defaultMessage: "Setup",
            })}
          </Tooltip>
          <Tooltip
            renderTrigger={({ props, ref }) => (
              <PlainButton
                className={cx(collapsedIconButtonClassName, "h-8 w-8")}
                ref={ref}
                {...props}
                onClick={() => getEmailConfig({ entityId })}
                disabled={isEmailConfigFetching}
              >
                {isEmailConfigFetching ? (
                  <LoadingSpinner size="xs" color="grey" />
                ) : (
                  <RefreshIcon className="h-3.5 w-3.5" />
                )}
              </PlainButton>
            )}
          >
            {t({
              id: "common.refresh",
              defaultMessage: "Refresh",
            })}
          </Tooltip>
        </div>
      )}
    </div>
  );
};
